import { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Card,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { createTaskTemplate, updateTaskTemplate } from "../Api/TaskTemplate";

const TaskTemplateModal = ({
  show,
  onHide,
  template,
  orgId,
  onTemplateSaved,
}) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId;
  const username = userDetails?.username;

  const [loading, setLoading] = useState(false);
  const [templateData, setTemplateData] = useState({
    templateName: "",
    templateDescription: "",
    includeFields: {},
  });

  // Available task fields configuration
  const availableFields = [
    {
      field: "taskName",
      displayName: "Task Name",
      type: "text",
      required: true,
    },
    {
      field: "taskDescription",
      displayName: "Task Description",
      type: "textarea",
      required: false,
    },
    {
      field: "assignedTo",
      displayName: "Assigned To",
      type: "select",
      required: false,
    },
    {
      field: "stageBelong",
      displayName: "Stage",
      type: "select",
      required: true,
    },
    {
      field: "taskStartDate",
      displayName: "Planned Start Date",
      type: "date",
      required: true,
    },
    {
      field: "taskEndDate",
      displayName: "Planned End Date",
      type: "date",
      required: true,
    },
    {
      field: "actualStartDate",
      displayName: "Actual Start Date",
      type: "date",
      required: false,
    },
    {
      field: "actualEndDate",
      displayName: "Actual End Date",
      type: "date",
      required: false,
    },
    {
      field: "taskType",
      displayName: "Task Type",
      type: "select",
      required: true,
    },
    {
      field: "taskPriority",
      displayName: "Priority",
      type: "select",
      required: true,
    },
    {
      field: "taskStatus",
      displayName: "Status",
      type: "select",
      required: true,
    },
    {
      field: "includeSubTasks",
      displayName: "Include Sub Tasks",
      type: "checkbox",
      required: false,
    },
  ];

  // Initialize form when modal opens or template changes
  useEffect(() => {
    if (show) {
      if (template) {
        // Edit mode - template.includeFields is already deserialized from the API
        setTemplateData({
          templateName: template.templateName || "",
          templateDescription: template.templateDescription || "",
          includeFields: template.includeFields || {},
        });
      } else {
        // Create mode
        setTemplateData({
          templateName: "",
          templateDescription: "",
          includeFields: {},
        });
      }
    }
  }, [show, template]);

  const handleFieldToggle = (fieldName, isRequired) => {
    setTemplateData((prev) => {
      const newIncludeFields = { ...prev.includeFields };

      if (isRequired) {
        newIncludeFields[fieldName] = "true"; // Store as string "true"
      } else {
        delete newIncludeFields[fieldName];
      }

      return {
        ...prev,
        includeFields: newIncludeFields,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!templateData.templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    if (Object.keys(templateData.includeFields).length === 0) {
      toast.error("Please select at least one field for the template");
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        orgId: String(orgId),
        templateName: templateData.templateName.trim(),
        templateDescription: templateData.templateDescription.trim(),
        includeFields: templateData.includeFields,
        createdBy: username || userId,
      };
      console.log("Submitting template data:", submitData);

      let result;
      if (template) {
        // Update existing template
        result = await updateTaskTemplate(template.templateId, {
          templateName: submitData.templateName,
          templateDescription: submitData.templateDescription,
          includeFields: submitData.includeFields,
          editedBy: username || userId,
        });
        toast.success("Task template updated successfully");
      } else {
        // Create new template
        result = await createTaskTemplate(submitData, username || userId);
        toast.success("Task template created successfully");
      }

      console.log("Template saved:", result);
      onTemplateSaved();
      onHide();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedFieldsCount = () => {
    return Object.keys(templateData.includeFields).length;
  };

  const getFieldDisplayName = (fieldName) => {
    const fieldConfig = availableFields.find((f) => f.field === fieldName);
    return fieldConfig?.displayName || fieldName;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {template ? "Edit Task Template" : "Create Task Template"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Template Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={templateData.templateName}
                  onChange={(e) =>
                    setTemplateData((prev) => ({
                      ...prev,
                      templateName: e.target.value,
                    }))
                  }
                  placeholder="Enter template name (e.g., 'Development Task', 'Quick Task')"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={templateData.templateDescription}
                  onChange={(e) =>
                    setTemplateData((prev) => ({
                      ...prev,
                      templateDescription: e.target.value,
                    }))
                  }
                  placeholder="Describe what this template should be used for..."
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <div className="mb-3">
                <Form.Label>
                  Select Fields for Template{" "}
                  <span className="text-danger">*</span>
                  <Badge bg="primary" className="ms-2">
                    {getSelectedFieldsCount()} selected
                  </Badge>
                </Form.Label>

                <Alert variant="info" className="py-2">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Select which fields should be included when using this
                    template. Required fields will be mandatory when creating
                    tasks.
                  </small>
                </Alert>

                <div className="fields-grid">
                  {availableFields.map((fieldConfig) => (
                    <Card
                      key={fieldConfig.field}
                      className={`field-card mb-2 ${
                        templateData.includeFields[fieldConfig.field]
                          ? "field-selected border-primary"
                          : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        handleFieldToggle(
                          fieldConfig.field,
                          !templateData.includeFields[fieldConfig.field]
                        )
                      }
                    >
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <h6 className="mb-1">
                              {fieldConfig.displayName}
                              {fieldConfig.required && (
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  className="ms-2"
                                >
                                  Required
                                </Badge>
                              )}
                            </h6>
                            <small className="text-muted">
                              Type: {fieldConfig.type}
                            </small>
                          </div>
                          <Form.Check
                            type="checkbox"
                            checked={
                              !!templateData.includeFields[fieldConfig.field]
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              handleFieldToggle(
                                fieldConfig.field,
                                e.target.checked
                              );
                            }}
                            disabled={fieldConfig.required}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {getSelectedFieldsCount() === 0 && (
                  <Alert variant="warning" className="mt-3">
                    <small>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Please select at least one field for the template
                    </small>
                  </Alert>
                )}

                {getSelectedFieldsCount() > 0 && (
                  <div className="mt-3">
                    <h6>Selected Fields:</h6>
                    <div className="d-flex flex-wrap gap-1">
                      {Object.keys(templateData.includeFields).map(
                        (fieldName) => (
                          <Badge key={fieldName} bg="success">
                            {getFieldDisplayName(fieldName)}
                          </Badge>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || getSelectedFieldsCount() === 0}
          >
            {loading
              ? "Saving..."
              : template
              ? "Update Template"
              : "Create Template"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default TaskTemplateModal;
